<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	//Services
	import { format } from 'svelte-i18n';
	import { zwaveBinarySwitchSet, zwaveLightSet } from '../../../services/xz_http_reqs';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	//Hooks
	import { beforeUpdate } from 'svelte';
	//Helpers
	import {
		input_status_check,
		get_z_wave_channel_structure_from_category,
		get_z_wave_ison,
		real_id,
	} from '../../../helpers/interface_helpers';
	import { getDeviceButtonInputIndicator } from '../../../helpers/input_state_display';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	import { calculate_timer } from '../../../helpers/timer_helpers';
	//Stores
	import { auth_locked_devices } from 'src/stores/device-runtime/local-auth';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	//Design system components
	import {
		Text,
		DeviceButton,
		toast,
		DeviceButtonV2,
		ControlsContainer,
		type DeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';

	//App components
	import PinUnlock from '../../../components/PinUnlock.svelte';
	import { hasControlRights } from 'src/helpers/user_helpers';

	let className = '';
	export { className as class };

	export let status,
		device,
		settings,
		isWide = false;
	export let useV2Components = false;
	export let deviceButtonV2Props: Partial<DeviceButtonV2PropsTypes> = {
		size: 's',
	};

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;

	let isLoading = false;
	let ison = false;
	let expected_ison = false;

	// Must be reactive — list cards share component instances / status by real_id
	$: component = get_z_wave_channel_structure_from_category(device);
	$: componentStatus = status?.[component];
	$: isLightComponent = component.startsWith('light:');
	$: if (!isLoading) {
		ison = get_z_wave_ison(componentStatus);
	}

	let hasTimer = false;
	let timer = { time: undefined, percentage: undefined };
	let timerStarted;
	let cacheTimerStarted;
	let tagline = '';

	let pinLocked;
	let showModal = false;
	let inputState: 'on' | 'off' | undefined;

	$: hasRights = hasControlRights(device);
	$: tagline = ison
		? $format('label_action_turn_off', { default: 'turn off' })
		: $format('label_action_turn_on', { default: 'turn on' });

	beforeUpdate(() => {
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

		if (status) {
			hasTimer =
				componentStatus?.duration?.value !== 0 &&
				typeof componentStatus?.duration?.value !== 'undefined';

			timerStarted = hasTimer ? componentStatus?.duration?.value : false;
			if (typeof cacheTimerStarted === 'undefined') {
				cacheTimerStarted = timerStarted;
			}
			if (timerStarted && hasTimer) {
				let [_, _time, _percentage] = calculate_timer(device, status, settings);
				if (Object.values(timer).some((t) => typeof t === 'undefined')) {
					timer.time = _time;
					timer.percentage = Number(_percentage.toFixed(0));
				}
				if (cacheTimerStarted !== timerStarted) {
					cacheTimerStarted = timerStarted;
					timer.time = _time;
					timer.percentage = Number(_percentage.toFixed(0));
				}
			} else {
				timer.time = undefined;
				timer.percentage = undefined;
			}
			inputState = getDeviceButtonInputIndicator($devices[device.id] ?? device, status, settings);
		} else {
			inputState = undefined;
		}
	});

	const handleTurn = async () => {
		if (!pinLocked) {
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}
			if (isLoading) return;
			isLoading = true;

			expected_ison = !ison;

			const res = isLightComponent
				? await zwaveLightSet(device, { on: expected_ison })
				: await zwaveBinarySwitchSet(device, expected_ison);

			if (res.isok) {
				ison = expected_ison;
				const nextStatus = { ...(status || {}) };
				const nextComponent = {
					...(nextStatus[component] || {}),
				};
				if (typeof nextComponent.currentValue !== 'undefined') {
					nextComponent.currentValue = expected_ison;
				} else if (typeof nextComponent.output !== 'undefined') {
					nextComponent.output = expected_ison;
				} else {
					nextComponent.output = expected_ison;
				}
				nextStatus[component] = nextComponent;

				devices_status.update_device_status({
					id: real_id(device.id),
					msg: nextStatus,
				});
			} else if ($auth_locked_devices.indexOf(real_id(device.id)) == -1) {
				toast.open({ type: 'danger', title: $format('error_global') });
			}
			isLoading = false;
		} else {
			showModal = true;
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleTurn();
	};

	const a11yProps = { timerAriaLabel: $format('label_turn_device', { default: 'Turn device' }) };
</script>

{#key device.id}
	{#if isWide && useV2Components}
		<ControlsContainer color="color-0">
			<svelte:fragment slot="first">
				<DeviceButtonV2
					id="devicebutton-zw-{device.id}"
					{isLoading}
					{hasTimer}
					{timer}
					indicator={inputState}
					isActive={ison}
					size="l"
					icon="fa fa-power-off"
					aria-label={$format('label_turn_device', { default: 'Turn device' })}
					{a11yProps}
					variant="primary"
					on:click={(e) => {
						e.stopPropagation();
						handleTurn();
					}}
					disabled={!status || !hasRights}
					class={className}
					{...deviceButtonV2Props}
				/>
				<Text styleType="controls-large" class="mt-s-plus text-bold">
					{capFirstChar(tagline)}
				</Text>
			</svelte:fragment>
		</ControlsContainer>
	{:else if useV2Components}
		<DeviceButtonV2
			id="devicebutton-zw-{device.id}"
			{isWide}
			{isLoading}
			{hasTimer}
			{timer}
			indicator={inputState}
			isActive={ison}
			icon="fa fa-power-off"
			aria-label={$format('label_turn_device', { default: 'Turn device' })}
			{a11yProps}
			variant="primary"
			on:click={(e) => {
				e.stopPropagation();
				handleTurn();
			}}
			disabled={!status || !hasRights}
			class={className}
			{...deviceButtonV2Props}
		/>
	{:else if isWide}
		<ControlsContainer>
			<svelte:fragment slot="first">
				<DeviceButton
					id="devicebutton-zw-{device.id}"
					{isWide}
					{isLoading}
					{hasTimer}
					{timer}
					indicator={inputState}
					isActive={ison}
					size="l"
					icon="fa fa-power-off"
					aria-label={$format('label_turn_device', { default: 'Turn device' })}
					{a11yProps}
					variant="primary"
					on:click={(e) => {
						e.stopPropagation();
						handleTurn();
					}}
					disabled={!status || !hasRights}
					class={className}
				/>
				<Text styleType="controls-large" class="mt-s text-bold">{capFirstChar(tagline)}</Text>
			</svelte:fragment>
		</ControlsContainer>
	{:else}
		<DeviceButton
			id="devicebutton-zw-{device.id}"
			{isWide}
			{isLoading}
			{hasTimer}
			{timer}
			indicator={inputState}
			isActive={ison}
			size="m"
			icon="fa fa-power-off"
			aria-label={$format('label_turn_device', { default: 'Turn device' })}
			{a11yProps}
			variant="primary"
			on:click={(e) => {
				e.stopPropagation();
				handleTurn();
			}}
			disabled={!status || !hasRights}
			class={className}
		/>
	{/if}
{/key}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
